import { useState, useEffect } from 'react';
import { getPendingCount } from './syncQueue.js';

/**
 * Visual indicator for online/offline status.
 * Green = online, Orange = offline (X ops pending), Red = sync error.
 * Fixed at the top of the screen.
 */
export default function OfflineIndicator() {
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(0);
  const [syncError, setSyncError] = useState(false);

  useEffect(() => {
    const handleOnline = () => { setOnline(true); setSyncError(false); };
    const handleOffline = () => setOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Poll pending count every 3s
  useEffect(() => {
    let active = true;
    const poll = async () => {
      if (!active) return;
      try {
        const count = await getPendingCount();
        setPending(count);
      } catch (_err) {
        setSyncError(true);
      }
    };
    poll();
    const interval = setInterval(poll, 3000);
    return () => { active = false; clearInterval(interval); };
  }, []);

  // Don't show anything when online with no pending ops
  if (online && pending === 0 && !syncError) return null;

  let bgColor = 'bg-green-500';
  let text = 'Connecté';

  if (syncError) {
    bgColor = 'bg-red-500';
    text = 'Erreur de sync';
  } else if (!online) {
    bgColor = 'bg-orange-500';
    text = pending > 0 ? `Hors ligne - ${pending} op. en attente` : 'Hors ligne';
  } else if (pending > 0) {
    bgColor = 'bg-orange-400';
    text = `Sync en cours - ${pending} op.`;
  }

  return (
    <div className={`fixed top-0 left-0 right-0 z-[100] ${bgColor} text-white text-center text-xs py-1 font-medium`}>
      {text}
    </div>
  );
}
